import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useApiCall, useInputText } from '@/hooks';
import { Validation } from '@/models';
import { COMMON_CONTAINER_ID, EMAIL_REGEX } from '@/config';
import {
  AlertBox,
  InputGroup,
  Input,
  InputTextarea,
  SectionBox,
  Button,
} from '@/components';
import { ContactFormRequest, ContactFormResponse, amlbotAPI } from '@/api';
import { toast } from 'react-toastify';
import styles from './Contact.module.sass';

interface ContactProps {}

const Contact: FC<ContactProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const FIELD_PRESENT_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const EMAIL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'email',
        value: EMAIL_REGEX,
        message: t('translation.invalid_email'),
      },
      {
        name: 'maxLength',
        value: 200,
        message: t('translation.max_length_error', { length: 200 }),
      },
    ],
    [t],
  );

  const nameField = useInputText('', FIELD_PRESENT_VALIDATIONS);
  const emailField = useInputText('', EMAIL_VALIDATIONS);
  const topicField = useInputText('', FIELD_PRESENT_VALIDATIONS);
  const messageField = useInputText('', FIELD_PRESENT_VALIDATIONS);

  const [sendContact, isLoading, contactError, contactErrorCode] = useApiCall(
    async () => {
      const payload: ContactFormRequest = {
        fullName: nameField.value,
        emailAddress: emailField.value,
        topic: topicField.value,
        message: messageField.value,
      };
      // Optionally [errorCode] - ErrorCodeEnum.FIELD_REQUIRED
      const result: ContactFormResponse =
        await amlbotAPI.sendContactForm(payload);

      if (result.success) {
        toast.success(t('translation.contact_us_success_msg_text'), {
          containerId: COMMON_CONTAINER_ID,
        });

        emailField.onReset();
        nameField.onReset();
        topicField.onReset();
        messageField.onReset();
      }
    },
  );

  const submitHandler = () => {
    if (!isLoading) {
      if (
        emailField.isValid &&
        nameField.isValid &&
        topicField.isValid &&
        messageField.isValid
      ) {
        sendContact();
      } else {
        if (!emailField.isValid) {
          emailField.onBlur();
        }

        if (!nameField.isValid) {
          nameField.onBlur();
        }

        if (!topicField.isValid) {
          topicField.onBlur();
        }

        if (!messageField.isValid) {
          messageField.onBlur();
        }
      }
    }
  };

  const onReturn = () => {
    navigate('/');
  };

  return (
    <SectionBox>
      {{
        title: (
          <div className={styles.sectionTitle}>
            <div>{t('translation.contact_us_title')}</div>
            <div className={styles.subtitle}>
              {t('translation.contact_us_top_text')}
            </div>
          </div>
        ),
        body: (
          <div className={styles.form}>
            {!!contactError && (
              <AlertBox type="danger">{{ description: contactError }}</AlertBox>
            )}

            <InputGroup
              label={t('translation.user_fullname_label')}
              className={styles.inputGroup}
            >
              <Input
                id="name"
                name="name"
                type="text"
                value={nameField.value}
                onChange={nameField.onChange}
                onBlur={nameField.onBlur}
                placeholder={t('translation.name_placeholder')!}
                disabled={isLoading}
                isError={nameField.isDirty && !nameField.isValid}
                required
              />
              {nameField.isDirty && !nameField.isValid && (
                <AlertBox type="danger" small>
                  {{ description: nameField.errors[0] }}
                </AlertBox>
              )}
            </InputGroup>

            <InputGroup
              label={t('translation.user_email_label')}
              className={styles.inputGroup}
            >
              <Input
                id="email"
                name="email"
                type="text"
                value={emailField.value}
                onChange={emailField.onChange}
                onBlur={emailField.onBlur}
                placeholder={t('translation.email_placeholder')!}
                disabled={isLoading}
                isError={emailField.isDirty && !emailField.isValid}
                required
              />
              {emailField.isDirty && !emailField.isValid && (
                <AlertBox type="danger" small>
                  {{ description: emailField.errors[0] }}
                </AlertBox>
              )}
            </InputGroup>

            <InputGroup
              label={t('translation.contact_us_topic_label')}
              className={styles.inputGroup}
            >
              <Input
                id="topic"
                name="topic"
                type="text"
                value={topicField.value}
                onChange={topicField.onChange}
                onBlur={topicField.onBlur}
                placeholder={t('translation.contact_us_topic_placeholder')!}
                disabled={isLoading}
                isError={topicField.isDirty && !topicField.isValid}
                required
              />
              {topicField.isDirty && !topicField.isValid && (
                <AlertBox type="danger" small>
                  {{ description: topicField.errors[0] }}
                </AlertBox>
              )}
            </InputGroup>

            <InputGroup
              label={t('translation.contact_us_msg_label')}
              className={styles.inputGroup}
            >
              <InputTextarea
                id="message"
                name="message"
                value={messageField.value}
                onChange={messageField.onChange}
                onBlur={messageField.onBlur}
                placeholder={t('translation.contact_us_msg_placeholder')!}
                disabled={isLoading}
                isError={messageField.isDirty && !messageField.isValid}
                required
              />
              {messageField.isDirty && !messageField.isValid && (
                <AlertBox type="danger" small>
                  {{ description: messageField.errors[0] }}
                </AlertBox>
              )}
            </InputGroup>
            <div className={styles.actions}>
              <Button
                type="primary"
                corner="rect"
                disabled={isLoading}
                isLoading={isLoading}
                onClick={submitHandler}
              >
                {t('translation.contact_us_send_btn')}
              </Button>
              <Button
                type="secondary"
                corner="rect"
                disabled={isLoading}
                onClick={onReturn}
              >
                {t('translation.contact_us_return_btn')}
              </Button>
            </div>
          </div>
        ),
      }}
    </SectionBox>
  );
};

export default Contact;
